import React, { useContext, useState, useEffect, useRef } from 'react';
import { SocketContext } from '../../../App';
import images from '../../../constants/diceImages';
import styles from './Dice.module.css';
import rollingGif from '../../../images/dice/rolling.gif';

const Dice = ({ rolledNumber, setRolledNumber, nowMoving, playerColor, movingPlayer, moveSeqRef, alwaysVisible = false }) => {
    const socket = useContext(SocketContext);
    const [isRolling, setIsRolling] = useState(false);
    const [landed, setLanded] = useState(false);
    const [lastRollStatus, setLastRollStatus] = useState('idle');
    const [lastRollReason, setLastRollReason] = useState('');
    const prevRolledRef = useRef(rolledNumber);
    const pendingRollRef = useRef(false);
    const landedTimeoutRef = useRef(null);
    const rollTimeoutRef = useRef(null);
    const hasRolledNumber = rolledNumber !== null && rolledNumber !== undefined;

    const clearPendingRoll = () => {
        pendingRollRef.current = false;
        setIsRolling(false);
        if (rollTimeoutRef.current) {
            clearTimeout(rollTimeoutRef.current);
            rollTimeoutRef.current = null;
        }
    };

    // Detect when a new number lands → trigger land animation
    useEffect(() => {
        if (rolledNumber !== null && rolledNumber !== undefined && rolledNumber !== prevRolledRef.current) {
            clearPendingRoll();
            setLanded(true);
            if (landedTimeoutRef.current) clearTimeout(landedTimeoutRef.current);
            // Clear landed state faster to allow immediate interaction
            landedTimeoutRef.current = setTimeout(() => setLanded(false), 100);
            prevRolledRef.current = rolledNumber;
            return undefined;
        }
        prevRolledRef.current = rolledNumber;
    }, [rolledNumber]);

    useEffect(() => {
        if (!nowMoving || hasRolledNumber) {
            clearPendingRoll();
        }
    }, [nowMoving, hasRolledNumber]);

    useEffect(() => () => {
        if (landedTimeoutRef.current) clearTimeout(landedTimeoutRef.current);
        if (rollTimeoutRef.current) clearTimeout(rollTimeoutRef.current);
    }, []);

    const handleClick = () => {
        if (!canRoll || isRolling || pendingRollRef.current || typeof socket?.emit !== 'function') return;
        pendingRollRef.current = true;
        setIsRolling(true);
        setLanded(false);
        setLastRollStatus('pending');
        setLastRollReason('');
        const currentSeq = moveSeqRef ? moveSeqRef.current : undefined;
        const payload = currentSeq === undefined ? {} : { seq: currentSeq };
        socket.emit('game:roll', payload, result => {
            if (!result || result.ok !== true) {
                clearPendingRoll();
                setLastRollStatus('rejected');
                setLastRollReason(String(result?.reason || 'no_ack'));
                const authoritativeSeq = Number(result?.moveSeq);
                if (moveSeqRef && Number.isFinite(authoritativeSeq)) {
                    moveSeqRef.current = authoritativeSeq;
                }
                const authoritativeRoll = Number(result?.rolledNumber);
                if (Number.isInteger(authoritativeRoll) && authoritativeRoll >= 1 && authoritativeRoll <= 6) {
                    setRolledNumber(authoritativeRoll);
                } else {
                    socket.emit('room:data');
                }
                return;
            }
            setLastRollStatus('accepted');
            setLastRollReason('');
            const nextSeq = Number(result.moveSeq);
            if (moveSeqRef && Number.isFinite(nextSeq)) moveSeqRef.current = nextSeq;
            const nextRoll = Number(result.rolledNumber);
            if (Number.isInteger(nextRoll) && nextRoll >= 1 && nextRoll <= 6) {
                setRolledNumber(nextRoll);
            }
        });
        if (rollTimeoutRef.current) clearTimeout(rollTimeoutRef.current);
        rollTimeoutRef.current = setTimeout(() => {
            clearPendingRoll();
            setLanded(false);
            if (!hasRolledNumber) {
                socket.emit('room:data');
            }
        }, 1800);
    };

    const isCurrentPlayer = movingPlayer === playerColor;
    const canRoll = isCurrentPlayer && nowMoving && !hasRolledNumber;
    const shouldRender = alwaysVisible || isCurrentPlayer;

    if (!shouldRender) return null;

    const imageSrc = isRolling
        ? rollingGif
        : hasRolledNumber
            ? images[rolledNumber - 1]
            : canRoll
                ? images[6]
                : null;
    const imageAlt = hasRolledNumber ? String(rolledNumber) : 'roll';

    const imgClass = [
        isRolling ? styles.rolling : '',
        landed ? styles.landed : '',
        canRoll && !isRolling ? styles.canRoll : '',
    ].filter(Boolean).join(' ');

    const containerClass = [
        styles.container,
        canRoll && !isRolling ? styles.yourTurn : '',
    ].filter(Boolean).join(' ');

    return (
        <div
            className={containerClass}
            data-testid="dice-control"
            data-roll-status={lastRollStatus}
            data-roll-reason={lastRollReason}
            data-can-roll={String(canRoll)}
            data-moving-player={movingPlayer || ''}
            data-player-color={playerColor || ''}
            data-move-seq={moveSeqRef?.current ?? ''}
            role="button"
            tabIndex={canRoll ? 0 : -1}
            aria-label={canRoll ? 'Roll dice' : 'Dice unavailable'}
            aria-disabled={!canRoll || isRolling || pendingRollRef.current}
            onClick={handleClick}
            onKeyDown={event => {
                if ((event.key === 'Enter' || event.key === ' ') && canRoll) {
                    event.preventDefault();
                    handleClick();
                }
            }}
        >
            {imageSrc ? (
                <img
                    src={imageSrc}
                    className={imgClass}
                    alt={imageAlt}
                    draggable={false}
                    style={{ pointerEvents: 'none' }}
                />
            ) : (
                <div style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: 10,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(255,255,255,0.08)',
                    color: 'rgba(255,255,255,0.4)',
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    userSelect: 'none',
                }}>
                    WAIT
                </div>
            )}
        </div>
    );
};

export default Dice;
